/**
 * msi-corp_element-guard.js
 * ---------------------------------------------------------------------------
 * 通用「元素守門員」：在某件事完成之前，以**無障礙**的方式暫時停用一組元素，
 * 完成（或逾時）後強制恢復可用，並在一段時間內防止第三方把它鎖回去。
 *
 * 與任何特定 App 無關。典型用途：
 *   - 發票／金流外掛初始化期間，避免顧客按下結帳造成半套訂單
 *   - 折扣外掛改寫價格期間，避免顧客按下加購
 *
 * 【硬性契約：fail-open】
 * 呼叫端保證會在逾時後呼叫 `release()`；本模組任何分支（含斷路器跳閘）
 * 都不可產生「偵測不到就永遠鎖住」的路徑。
 *
 * 【無障礙 (WCAG 2.1 AA / EN 301 549)】
 * 鎖定期間不使用 `disabled` 屬性或 `pointer-events: none`，改用
 * `aria-disabled` + `aria-busy` + 捕獲階段事件攔截，確保元素仍可被鍵盤聚焦，
 * 且狀態變化會透過 `role="status"` live region 通知螢幕閱讀器。
 * 載入指示器標記 `aria-hidden` 並遵守 `prefers-reduced-motion`。
 *
 * 【GDPR】不讀寫 cookie / storage，不蒐集也不傳送任何個人資料。
 * ---------------------------------------------------------------------------
 */

const SR_ONLY_CLASS = 'msi-guard-sr-only';
const SPINNER_CLASS = 'msi-guard-spinner';
const STYLE_ID = 'msi-guard-style';
const LOCKED_ATTR = 'data-msi-guard-locked';

/** 預設設定；呼叫端只需覆寫在意的欄位。 */
export const GUARD_DEFAULTS = Object.freeze({
  /** 要守護的元素選擇器（支援多個，例如 cart drawer 與 cart page 各一顆）。 */
  selector: '',
  /** 解鎖後持續監看、防止被鎖回去的時間（毫秒）。 */
  enforceMs: 20000,
  /** 鎖定期間是否在元素內顯示載入指示器。 */
  showSpinner: true,
  /**
   * 失控迴圈斷路器：`runawayWindowMs` 內 applyState 超過此次數即停機。
   * ⚠️ `scheduleApply()` 用 requestAnimationFrame 節流，applyState 實際呼叫頻率
   * 天花板等於螢幕更新率（常見 60Hz ≈ 60 次/秒，且會被背景分頁進一步降頻）。
   * 此門檻必須明顯低於該天花板才有可能被觸發到，否則斷路器形同虛設、永遠不跳閘。
   */
  runawayLimit: 40,
  runawayWindowMs: 1000,
  /** 斷路後改用的低頻輪詢間距；0 為完全停止執行（毫秒）。 */
  fallbackIntervalMs: 500,
  /** 螢幕閱讀器播報文字。 */
  lockedMessage: '功能載入中，請稍候。',
  readyMessage: '功能已就緒。',
});

function ensureStyleSheet() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .${SR_ONLY_CLASS} {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      margin: -1px !important;
      padding: 0 !important;
      border: 0 !important;
      overflow: hidden !important;
      clip: rect(0 0 0 0) !important;
      clip-path: inset(50%) !important;
      white-space: nowrap !important;
    }
    .${SPINNER_CLASS} {
      display: inline-block;
      flex: none;
      box-sizing: border-box;
      width: 1em;
      height: 1em;
      margin-inline-end: .5em;
      vertical-align: -.125em;
      border: 2px solid currentColor;
      border-block-start-color: transparent;
      border-radius: 50%;
      animation: msi-guard-spin .7s linear infinite;
    }
    @keyframes msi-guard-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) {
      /* 尊重使用者的減少動態偏好；狀態改由 aria-busy 與 live region 傳達 */
      .${SPINNER_CLASS} { animation: none; border-block-start-color: currentColor; opacity: .5; }
    }
  `;
  document.head.appendChild(style);
}

/** 每個守門員有自己的 live region，避免多個 App 互相蓋掉播報內容。 */
function ensureLiveRegion(id) {
  let region = document.getElementById(id);
  if (region) return region;

  ensureStyleSheet();
  region = document.createElement('div');
  region.id = id;
  region.className = SR_ONLY_CLASS;
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  document.body.appendChild(region);
  return region;
}

/**
 * 建立守門員。
 * @param {Partial<typeof GUARD_DEFAULTS> & {selector: string}} options
 * @param {ReturnType<import('./msi-corp_debug-logger.js').createLogger>} logger
 * @param {string} [instanceId] 用於區隔多個守門員的 live region id
 */
export function createElementGuard(options, logger, instanceId = 'default') {
  const config = { ...GUARD_DEFAULTS, ...options };
  const liveRegionId = `msi-guard-status--${instanceId}`;

  let locked = false;
  let watching = false;
  let observer = null;
  let enforceTimer = null;
  let fallbackTimer = null;
  let scheduled = false;
  let lastAnnounced = '';
  let sawElement = false;
  /** 斷路器跳閘後永久繞過計數：此時 observer 已斷開、不再有迴圈風險，
   *  但仍需能寫入 DOM 才能完成 fail-open 解鎖。 */
  let bypassBreaker = false;

  /**
   * 失控迴圈斷路器。
   * applyState 本身是冪等的（值相同就不寫入），正常情況不會自我觸發；
   * 但若主題或第三方 App 另有 MutationObserver 反覆改寫元素，就會形成互鬥迴圈。
   * 跳閘時斷開 observer、改為低頻輪詢：切斷緊密回饋迴圈的同時，
   * 仍保證對手停手後元素最終一定可用（不能因為斷路就變成 fail-closed）。
   */
  const runaway = logger.watchRunaway('applyState', {
    limit: config.runawayLimit,
    windowMs: config.runawayWindowMs,
    onTrip: () => {
      bypassBreaker = true;
      stopWatching();
      if (locked) release('runaway-tripped');
      else applyState();
      startFallbackPolling();
      logger.error('已斷開 DOM 監看以中斷迴圈；目標元素改由低頻輪詢維持可用（fail-open）');
    },
  });

  const getElements = () => (config.selector ? Array.from(document.querySelectorAll(config.selector)) : []);

  const announce = (message) => {
    if (!message || message === lastAnnounced) return;
    lastAnnounced = message;
    ensureLiveRegion(liveRegionId).textContent = message;
    logger.debug('live region 播報', message);
  };

  const mountSpinner = (el) => {
    if (!config.showSpinner || el.querySelector(`.${SPINNER_CLASS}`)) return false;
    ensureStyleSheet();
    const spinner = document.createElement('span');
    spinner.className = SPINNER_CLASS;
    spinner.setAttribute('aria-hidden', 'true');
    el.prepend(spinner); // 以 prepend 插入，不觸碰元素原有的 innerHTML
    return true;
  };

  const unmountSpinner = (el) => {
    const spinner = el.querySelector(`.${SPINNER_CLASS}`);
    if (!spinner) return false;
    spinner.remove();
    return true;
  };

  /**
   * 套用目前狀態到所有目標元素。
   * 必須維持冪等（值相同就不寫入），否則會與監看用的 MutationObserver 形成無限迴圈。
   * @returns {number} 實際寫入 DOM 的次數；持續 > 0 代表有人在跟我們對抗
   */
  const applyState = () => {
    if (!bypassBreaker && !runaway.tick()) return 0;

    const elements = getElements();
    if (elements.length === 0) {
      if (sawElement) logger.debug('目標元素從 DOM 消失（可能是區塊重繪）');
      return 0;
    }
    if (!sawElement) {
      sawElement = true;
      logger.phase('element:found', { count: elements.length, selector: config.selector });
    }

    let writes = 0;
    for (const el of elements) {
      if (locked) {
        if (el.getAttribute('aria-disabled') !== 'true') { el.setAttribute('aria-disabled', 'true'); writes += 1; }
        if (el.getAttribute('aria-busy') !== 'true') { el.setAttribute('aria-busy', 'true'); writes += 1; }
        if (!el.hasAttribute(LOCKED_ATTR)) { el.setAttribute(LOCKED_ATTR, ''); writes += 1; }
        if (el.style.getPropertyValue('opacity') !== '0.65') { el.style.setProperty('opacity', '0.65', 'important'); writes += 1; }
        if (el.style.getPropertyValue('cursor') !== 'progress') { el.style.setProperty('cursor', 'progress', 'important'); writes += 1; }
        if (mountSpinner(el)) writes += 1;
        continue;
      }

      // 解鎖：同時拆除「屬性式」「行內樣式式」「class/CSS 式」三種鎖法。
      // 行內 !important 位於作者樣式串接的最頂端，可蓋過第三方任何 CSS 規則。
      if (el.hasAttribute('disabled')) {
        el.removeAttribute('disabled');
        writes += 1;
        logger.warn('偵測到第三方在解鎖後重新加上 disabled，已再次移除', { id: el.id || null });
      }
      if (el.hasAttribute('aria-disabled')) { el.removeAttribute('aria-disabled'); writes += 1; }
      if (el.hasAttribute('aria-busy')) { el.removeAttribute('aria-busy'); writes += 1; }
      if (el.hasAttribute(LOCKED_ATTR)) { el.removeAttribute(LOCKED_ATTR); writes += 1; }
      if (el.style.getPropertyValue('pointer-events') !== 'auto') { el.style.setProperty('pointer-events', 'auto', 'important'); writes += 1; }
      if (el.style.getPropertyValue('opacity') !== '1') { el.style.setProperty('opacity', '1', 'important'); writes += 1; }
      if (el.style.getPropertyValue('cursor') !== 'pointer') { el.style.setProperty('cursor', 'pointer', 'important'); writes += 1; }
      if (el.style.getPropertyValue('filter')) { el.style.removeProperty('filter'); writes += 1; }
      if (unmountSpinner(el)) writes += 1;
    }

    if (writes > 0) {
      const total = logger.count('domWrites');
      logger.debug(`套用狀態（locked=${locked}）寫入 ${writes} 項`, { elements: elements.length, totalWrites: total });
    }
    return writes;
  };

  const scheduleApply = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      applyState();
    });
  };

  /**
   * 互動攔截。
   * 掛在 document 的捕獲階段，因此元素被重新渲染（例如 cart drawer 重繪）也依然有效，
   * 且同時擋下滑鼠與鍵盤，符合無障礙一致性要求。
   */
  const interceptor = (event) => {
    if (!locked) return;
    const target = event.target instanceof Element ? event.target.closest(config.selector) : null;
    if (!target) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    event.stopPropagation();
    logger.info(`攔截到鎖定期間的 ${event.type}（第 ${logger.count('blockedInteractions')} 次）`);
    announce(config.lockedMessage);
  };

  /** 監看 DOM，讓稍後才渲染的元素也能套用狀態，並偵測第三方把元素鎖回去。 */
  function startWatching() {
    if (watching) return;
    watching = true;
    observer = new MutationObserver(scheduleApply);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class', 'style'],
    });
    logger.phase('watch:start');
  }

  function stopWatching() {
    clearTimeout(enforceTimer);
    if (!watching) return;
    watching = false;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    logger.phase('watch:stop', { domWrites: logger.counters().domWrites || 0, runawayPeak: runaway.peak });
  }

  /** 斷路後的降級模式：不再即時反應，改用有界的低頻輪詢確保最終解鎖。 */
  function startFallbackPolling() {
    if (fallbackTimer || config.enforceMs === 0 || config.fallbackIntervalMs === 0) return;
    fallbackTimer = setInterval(applyState, config.fallbackIntervalMs);
    setTimeout(stopFallbackPolling, config.enforceMs);
    logger.phase('fallback:start', { intervalMs: config.fallbackIntervalMs, durationMs: config.enforceMs });
  }

  function stopFallbackPolling() {
    if (!fallbackTimer) return;
    clearInterval(fallbackTimer);
    fallbackTimer = null;
    logger.phase('fallback:stop', { domWrites: logger.counters().domWrites || 0 });
  }

  function lock() {
    if (locked) return;
    locked = true;
    document.addEventListener('click', interceptor, true);
    document.addEventListener('keydown', interceptor, true);
    logger.phase('lock');
    applyState();
    startWatching();
  }

  /** @param {string} reason 解鎖原因，供除錯與事件酬載使用。 */
  function release(reason) {
    const wasLocked = locked;
    locked = false;
    document.removeEventListener('click', interceptor, true);
    document.removeEventListener('keydown', interceptor, true);
    logger.phase('release', { reason, wasLocked });
    applyState();

    if (config.enforceMs > 0 && !runaway.tripped) {
      startWatching();
      clearTimeout(enforceTimer);
      enforceTimer = setTimeout(() => {
        logger.phase('enforce:expired', { enforceMs: config.enforceMs });
        stopWatching();
      }, config.enforceMs);
    } else {
      stopWatching();
    }

    if (wasLocked) announce(config.readyMessage);
  }

  return {
    get locked() {
      return locked;
    },
    get selector() {
      return config.selector;
    },
    get diagnostics() {
      return {
        selector: config.selector,
        locked,
        watching,
        fallbackPolling: Boolean(fallbackTimer),
        elements: getElements().length,
        runawayTripped: runaway.tripped,
        runawayPeak: runaway.peak,
        counters: logger.counters(),
      };
    },
    lock,
    release,
    destroy() {
      stopWatching();
      stopFallbackPolling();
      document.removeEventListener('click', interceptor, true);
      document.removeEventListener('keydown', interceptor, true);
      logger.phase('destroy');
    },
  };
}
